import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import { AuthLayout } from '@/components/AuthLayout';
import { makeClient } from '@/lib/api';
import { useT } from '@/lib/i18n';

type State = 'loading' | 'ok' | 'error';

export function Verify() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>('loading');
  const navigate = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setState('error'); return; }
    makeClient()
      .POST('/auth/verify', { body: { token } })
      .then(({ error }) => {
        setState(error ? 'error' : 'ok');
        if (!error) setTimeout(() => navigate('/login'), 2500);
      })
      .catch(() => setState('error'));
  }, [token, navigate]);

  return (
    <AuthLayout subtitle={t('verify.subtitle')}>
      <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
        {state === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <Spinner className="size-7" />
            <p className="text-sm text-muted-foreground">{t('verify.validating')}</p>
          </div>
        )}
        {state === 'ok' && (
          <>
            <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-[#3c9a18]/12">
              <CheckCircle2 className="size-7 text-[#3c9a18]" />
            </div>
            <h2 className="text-xl font-bold">{t('verify.confirmedTitle')}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{t('verify.confirmedBody')}</p>
            <Button asChild className="mt-6 w-full"><Link to="/login">{t('verify.enterNow')}</Link></Button>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-destructive/12">
              <XCircle className="size-7 text-destructive" />
            </div>
            <h2 className="text-xl font-bold">{t('verify.invalidTitle')}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{t('verify.invalidBody')}</p>
            <Button asChild variant="outline" className="mt-6 w-full"><Link to="/signup">{t('verify.backToSignup')}</Link></Button>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
